/**
 * ReferencesCarousel component for displaying scrollable URL references
 * Features responsive design, smooth scrolling, and loading animations
 */
import React, {useRef, useState, useEffect, useCallback} from 'react';
import styled from 'styled-components/native';
import {TouchableOpacity, View, Text, Dimensions, Platform, ScrollView} from 'react-native';
import {useTheme} from 'styled-components';
import {Theme} from '../../theme/lightTheme';
import Animated, {
  FadeIn,
  FadeOut,
  useSharedValue,
  useAnimatedStyle,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';
import Icon from '../icon';
import {throttle} from 'lodash';
import useResponsiveDimensions from '../../utils/hooks/useResponsiveDimensions';
import {LinearGradient} from 'react-native-linear-gradient';
import ReferenceItem from '../referenceItem';
import {openUrl} from '../../utils/helpers';

/**
 * Main container for the carousel
 * Adds padding for navigation arrows and handles overflow
 */
const CarouselContainer = styled.View<{isRTL?: boolean}>`
  position: relative;
  width: 100%;
  margin-vertical: 16px;
  overflow: hidden;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

/**
 * Navigation arrow buttons for scrolling through references
 * Features translucent background
 */
const ArrowButton = styled(TouchableOpacity)<{
  position: 'left' | 'right';
  isRTL?: boolean;
}>`
  margin-left: ${({isRTL, position}) => (position === 'left' ? '0px' : '10px')};
  margin-right: ${({isRTL, position}) =>
    position === 'right' ? '0px' : '10px'};
  background-color: ${({theme}) => theme.cardBackground + 'cc'};
  border: 1px solid ${({theme}) => theme.text.light + '33'};
  border-radius: 20px;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  z-index: 4;
  opacity: ${({disabled}) => (disabled ? 0.7 : 1)};
`;

/**
 * Props interface for styled components that need visibility information
 */
interface ReferenceCardProps {
  /** Number of visible items in the carousel */
  visibleItems: number;
  screenWidth: number;
  contentPadding: number;
  itemGap: number;
}

/**
 * Loading spinner with continuous rotation animation
 */
const Spinner = styled(Animated.View)`
  width: 16px;
  height: 16px;
  border: 2px solid ${({theme}) => theme.text.light + '88'};
  border-top-color: transparent;
  border-radius: 8px;
`;

/**
 * Placeholder card while loading
 */
const SkeletonCard = styled(Animated.View)<ReferenceCardProps>`
  width: ${({visibleItems, screenWidth, contentPadding, itemGap}) =>
    (screenWidth - itemGap * (visibleItems - 1) - contentPadding * 2) /
    visibleItems}px;
  height: 120px;
  border-radius: 12px;
  overflow: hidden;
  background-color: ${({theme}) => theme.skeletonBackground};
`;

// Removed internal styled ReferenceItem in favor of dedicated component

const ReferenceTitle = styled.Text`
  font-size: ${({theme}) => theme.text.s9}px;
  font-weight: 600;
  color: ${({theme}) => theme.text.dark};
  margin-bottom: 8px;
`;

const ReferenceUrl = styled.Text`
  font-size: ${({theme}) => theme.text.s11}px;
  color: ${({theme}) => theme.text.grey};
`;

/**
 * Shuffles array elements randomly
 * Used to randomize reference order
 *
 * @param array - Array to shuffle
 * @returns New shuffled array
 */
const shuffleArray = (array: string[]): string[] => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

/**
 * Props interface for the ReferencesCarousel component
 */
interface ReferencesCarouselProps {
  /** Array of URLs to display as reference cards or objects containing links */
  references: Array<
    | string
    | {
        full_link?: string;
        genio_url?: string;
        website_name?: string;
        show_name?: boolean;
      }
  >;
  /** Whether to show loading animation (default: true) */
  simulateLoading?: boolean;
  /** Whether to display in right-to-left mode */
  isRTL?: boolean;
}

// Normalized item type
interface NormalizedReferenceItem {
  url: string;
  displayName: string;
}

/**
 * ReferencesCarousel component for displaying scrollable URL references
 * Features responsive design, loading states, and navigation controls
 */
const ReferencesCarousel: React.FC<ReferencesCarouselProps> = ({
  references,
  simulateLoading = true,
  isRTL = false,
}) => {
  const carouselRef = useRef<ScrollView>(null);
  const theme = useTheme() as Theme;
  const {wdp, isMobile} = useResponsiveDimensions();
  // We'll determine the number of visible items based on the window width.
  const [visibleItems, setVisibleItems] = useState(3);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [orderedReferences, setOrderedReferences] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(simulateLoading);
  const [revealedCount, setRevealedCount] = useState(0);
  const urlsSignatureRef = useRef<string>('');
  const [items, setItems] = useState<NormalizedReferenceItem[]>([]);
  const [containerWidth, setContainerWidth] = useState(
    Math.max(0, Dimensions.get('window').width - 80),
  );
  const [viewportWidth, setViewportWidth] = useState<number>(0);
  const CONTENT_PADDING = 8;
  const ITEM_GAP = 16;
  const [isDragging, setIsDragging] = useState(false);
  const scrollXRef = useRef(0);

  // Reanimated values for skeleton shimmer
  const skeletonOpacity = useSharedValue(0.3);
  useEffect(() => {
    skeletonOpacity.value = withRepeat(
      withTiming(0.6, {duration: 800}),
      -1,
      true,
    );
  }, [skeletonOpacity]);
  const skeletonAnimatedStyle = useAnimatedStyle(() => ({
    opacity: skeletonOpacity.value,
  }));

  // Handle resize
  useEffect(() => {
    const updateVisibleItems = throttle(() => {
      const {width} = Dimensions.get('window');
      setContainerWidth(Math.max(0, width - 80));
      if (width < 1070) {
        setVisibleItems(1);
      } else if (width < 1600) {
        setVisibleItems(3);
      } else {
        setVisibleItems(5);
      }
    }, 100);

    updateVisibleItems();
    const subscription = Dimensions.addEventListener('change', updateVisibleItems);
    return () => {
      subscription?.remove();
      updateVisibleItems.cancel();
    };
  }, []);

  // Normalize references
  useEffect(() => {
    const normalized: NormalizedReferenceItem[] = (references || [])
      .map(r => {
        if (typeof r === 'string') {
          let display = r;
          try {
            display = new URL(r).hostname;
          } catch {}
          return {url: r, displayName: display};
        }
        const url = r.full_link || r.genio_url || '';
        const displayName = r.show_name && r.website_name ? r.website_name : (() => {
          try { return url ? new URL(url).hostname : ''; } catch { return url; }
        })();
        return {url, displayName};
      })
      .filter(i => !!i.url);

    const urls = normalized.map(i => i.url);
    const signature = urls.join('|');
    
    if (signature === urlsSignatureRef.current) {
      return;
    }
    
    urlsSignatureRef.current = signature;
    setItems(normalized);
    setOrderedReferences(urls);
    const shouldLoad = simulateLoading && normalized.length > 0;
    setIsLoading(shouldLoad);
    // Always start from 0 when new references arrive to trigger progressive reveal
    if (shouldLoad) {
      setRevealedCount(0);
    } else {
      setRevealedCount(normalized.length);
    }
  }, [references, simulateLoading]);

  // Progressive reveal of reference cards to mask slow metadata fetch
  useEffect(() => {
    if (!isLoading || items.length === 0) {
      return;
    }
    
    const interval = setInterval(() => {
      setRevealedCount(prev => {
        const next = Math.min(items.length, (prev || 0) + 1);
        
        // Auto-scroll to show the next loading skeleton
        if (next < items.length && carouselRef.current) {
          const gap = ITEM_GAP;
          const cw = Math.max(0, viewportWidth || containerWidth);
          const itemWidth =
            (cw - gap * (visibleItems - 1) - CONTENT_PADDING * 2) / visibleItems;
          
          // Calculate scroll position to show the next item (the skeleton)
          // We want to scroll so the next skeleton is visible at the edge
          const scrollToPosition = Math.max(0, (next - visibleItems + 1) * (itemWidth + gap));
          
          carouselRef.current.scrollTo({
            x: scrollToPosition,
            y: 0,
            animated: true,
          });
        }
        
        if (next >= items.length) {
          clearInterval(interval);
          setIsLoading(false);
          
          // After loading completes, scroll back to the beginning to allow left navigation
          setTimeout(() => {
            if (carouselRef.current) {
              carouselRef.current.scrollTo({
                x: 0,
                y: 0,
                animated: true,
              });
              setCurrentIndex(0);
            }
          }, 500); // Small delay before scrolling back
        }
        return next;
      });
    }, 1000); // 1 second delay between items
    return () => clearInterval(interval);
  }, [isLoading, items.length, visibleItems, containerWidth, viewportWidth]);

  // Scroll calculations
  const scrollToIndex = useCallback(
    (index: number) => {
      const gap = ITEM_GAP;
      const cw = Math.max(0, viewportWidth || containerWidth);
      const itemWidth =
        (cw - gap * (visibleItems - 1) - CONTENT_PADDING * 2) / visibleItems;
      const totalContentWidth =
        orderedReferences.length * itemWidth + (orderedReferences.length - 1) * gap;

      let offset = index * (itemWidth + gap);
      if (isRTL) {
        const maxOffset = Math.max(0, totalContentWidth - cw);
        offset = Math.max(0, maxOffset - offset);
      }

      carouselRef.current?.scrollTo({x: offset, y: 0, animated: true});
      setCurrentIndex(index);
    },
    [isRTL, visibleItems, orderedReferences.length, containerWidth, viewportWidth],
  );

  const scrollNext = useCallback(() => {
    if (!orderedReferences.length) return;
    const nextIndex = Math.min(
      currentIndex + visibleItems,
      Math.max(0, orderedReferences.length - visibleItems),
    );
    scrollToIndex(nextIndex);
  }, [currentIndex, orderedReferences.length, visibleItems, scrollToIndex]);

  const scrollPrev = useCallback(() => {
    if (!orderedReferences.length) return;
    const prevIndex = Math.max(0, currentIndex - visibleItems);
    scrollToIndex(prevIndex);
  }, [currentIndex, visibleItems, orderedReferences.length, scrollToIndex]);

  // Skeletons while loading
  const renderSkeletons = () => {
    return Array(visibleItems)
      .fill(null)
      .map((_, index) => (
        <Animated.View
          key={`skeleton-${index}`}
          entering={FadeIn}
          exiting={FadeOut}
          style={{marginRight: index !== visibleItems - 1 ? ITEM_GAP : 0}}>
          <SkeletonCard
            screenWidth={viewportWidth || containerWidth}
            visibleItems={visibleItems}
            contentPadding={CONTENT_PADDING}
            itemGap={ITEM_GAP}
            style={skeletonAnimatedStyle}>
            <LinearGradient
              colors={[theme.chat.startLinearGradient, theme.chat.endLinearGradient, theme.shine]}
              start={{x: 0, y: 0.5}}
              end={{x: 1, y: 0.5}}>
              <View style={{flex: 1, height: '100%', width: '100%'}} />
            </LinearGradient>
          </SkeletonCard>
        </Animated.View>
      ));
  };

  return (
    <CarouselContainer
      isRTL={isRTL}
      onLayout={e => {
        const w = e.nativeEvent.layout.width;
        if (w && Math.abs(w - containerWidth) > 1) {
          setContainerWidth(w);
        }
      }}>
      <ArrowButton
        position="left"
        isRTL={isRTL}
        onPress={scrollPrev}
        disabled={currentIndex === 0 || isLoading}
        accessibilityLabel="Previous references">
        <Icon name="back" size={14} color={theme.text.grey} />
      </ArrowButton>
      <ScrollView
        ref={carouselRef}
        horizontal
        scrollEnabled={!isLoading}
        showsHorizontalScrollIndicator={false}
        style={{flex: 1}}
        bounces
        overScrollMode="always"
        scrollEventThrottle={16}
        onLayout={e => setViewportWidth(e.nativeEvent.layout.width)}
        onScroll={e => {
          scrollXRef.current = e.nativeEvent.contentOffset.x;
          
          // Update currentIndex based on scroll position for arrow states
          if (!isLoading && items.length > 0) {
            const gap = ITEM_GAP;
            const cw = Math.max(0, viewportWidth || containerWidth);
            const itemWidth =
              (cw - gap * (visibleItems - 1) - CONTENT_PADDING * 2) / visibleItems;
            const scrollX = e.nativeEvent.contentOffset.x;
            const calculatedIndex = Math.round(scrollX / (itemWidth + gap));
            setCurrentIndex(calculatedIndex);
          }
        }}
        onScrollBeginDrag={() => setIsDragging(true)}
        onScrollEndDrag={() => setIsDragging(false)}
        onMomentumScrollBegin={() => setIsDragging(true)}
        onMomentumScrollEnd={() => setIsDragging(false)}
        directionalLockEnabled
        {...(Platform.OS === 'web'
          ? {
              // @ts-ignore - RN Web wheel event passthrough
              onWheel: (e: any) => {
                // Prefer vertical wheel for horizontal scrolling in this horizontal list
                const delta = Math.abs(e.deltaY) > Math.abs(e.deltaX)
                  ? e.deltaY
                  : e.deltaX;
                const next = Math.max(0, scrollXRef.current + delta);
                e.preventDefault?.();
                carouselRef.current?.scrollTo({x: next, y: 0, animated: false});
              },
            }
          : {})}
        contentContainerStyle={{
          paddingBottom: 8,
          alignItems: 'center',
          paddingHorizontal: CONTENT_PADDING,
        }}>
        {items.length === 0
          ? null
          : isLoading
            ? [
                ...items.slice(0, revealedCount).map((item, index) => (
                  <Animated.View
                    key={`${item.url}-${index}`}
                    entering={FadeIn}
                    exiting={FadeOut}
                    style={{marginRight: index !== items.length - 1 ? ITEM_GAP : 0}}>
                    <ReferenceItem
                      href={item.url}
                      visibleItems={visibleItems}
                      screenWidth={viewportWidth || containerWidth}
                      contentPadding={CONTENT_PADDING}
                      itemGap={ITEM_GAP}
                      // Disable press while dragging to prevent accidental opens
                      disabled={isDragging}
                    />
                  </Animated.View>
                )),
                ...Array(Math.max(0, items.length - revealedCount))
                  .fill(null)
                  .map((_, index) => (
                    <Animated.View
                      key={`skeleton-${index}`}
                      entering={FadeIn}
                      exiting={FadeOut}
                      style={{marginRight: index !== visibleItems - 1 ? ITEM_GAP : 0}}>
                      <SkeletonCard
                        screenWidth={viewportWidth || containerWidth}
                        visibleItems={visibleItems}
                        contentPadding={CONTENT_PADDING}
                        itemGap={ITEM_GAP}
                        style={skeletonAnimatedStyle}>
                        <LinearGradient
                          colors={[theme.chat.startLinearGradient, theme.chat.endLinearGradient, theme.shine]}
                          start={{x: 0, y: 0.5}}
                          end={{x: 1, y: 0.5}}>
                          <View style={{flex: 1, height: '100%', width: '100%'}} />
                        </LinearGradient>
                      </SkeletonCard>
                    </Animated.View>
                  )),
              ]
            : items.map((item, index) => (
                <Animated.View
                  key={`${item.url}-${index}`}
                  entering={FadeIn}
                  exiting={FadeOut}
                  style={{marginRight: index !== items.length - 1 ? ITEM_GAP : 0}}>
                  <ReferenceItem
                    href={item.url}
                    visibleItems={visibleItems}
                    screenWidth={viewportWidth || containerWidth}
                    contentPadding={CONTENT_PADDING}
                    itemGap={ITEM_GAP}
                    // Disable press while dragging to prevent accidental opens
                    disabled={isDragging}
                  />
                </Animated.View>
              ))}
      </ScrollView>
      {/* Next button */}
      <ArrowButton
        position="right"
        isRTL={isRTL}
        onPress={scrollNext}
        disabled={
          currentIndex >= Math.max(0, orderedReferences.length - visibleItems) ||
          orderedReferences.length <= visibleItems ||
          isLoading
        }
        style={{transform: [{rotate: '180deg'}]}}
        accessibilityLabel="Next references">
        <Icon name="back" size={14} color={theme.text.grey} />
      </ArrowButton>
    </CarouselContainer>
  );
};

export default ReferencesCarousel;
